import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';

import { ADMIN_DELETE_USER } from '../recovery/graphql';

// Typed locally so this compiles before codegen regenerates __gqltypes__ from
// the updated server schema.
type AdminDeleteUserResult = {
  adminDeleteUser: { success: boolean; message: string | null };
};
type AdminDeleteUserVars = { input: { userId: string } };

// Minimal, source-agnostic props so this modal works both from the Account
// Recovery search results (AdminUserSummary) and the devtools User View
// (SearchUserQuery.user), which expose different field names.
type Props = {
  userId: string;
  displayName: string;
  email?: string | null;
  firebaseEmail?: string | null;
  providers?: string[];
  onHide: () => void;
  onSuccess?: () => void;
};

// Deleting an account is irreversible — there is no tool to restore it. Two
// gates guard it: 1) type the word DELETE + acknowledge, 2) an "are you sure"
// screen where a single button fires the delete.
const DELETE_KEYWORD = 'DELETE';

export default function DeleteUserModal({
  userId,
  displayName,
  email,
  firebaseEmail,
  providers,
  onHide,
  onSuccess,
}: Props): JSX.Element {
  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const [confirmText, setConfirmText] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [result, setResult] = useState<{ variant: 'success' | 'danger'; text: string } | null>(null);

  const [deleteUser, { loading }] = useMutation<AdminDeleteUserResult, AdminDeleteUserVars>(ADMIN_DELETE_USER);

  // Gate 1: type the DELETE keyword (+ acknowledge) to arm the flow.
  const deleteWordMatches = confirmText.trim().toUpperCase() === DELETE_KEYWORD;
  const canContinue = deleteWordMatches && acknowledged && !loading && !result;
  // Gate 3: the final confirmation button.
  const canDelete = !loading && !result;

  const handleContinue = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canContinue) return;
    setStep('confirm');
  };

  const handleConfirmDelete = async () => {
    if (!canDelete) return;
    try {
      const { data } = await deleteUser({ variables: { input: { userId } } });
      if (data?.adminDeleteUser.success) {
        setResult({
          variant: 'success',
          text: data.adminDeleteUser.message ?? 'Account deleted.',
        });
        setTimeout(() => {
          onSuccess?.();
          onHide();
        }, 1500);
      } else {
        setResult({
          variant: 'danger',
          text: data?.adminDeleteUser.message ?? 'Unknown error.',
        });
      }
    } catch (err) {
      setResult({ variant: 'danger', text: (err as Error).message });
    }
  };

  const title = step === 'form' ? 'Delete account' : 'Delete account — are you sure?';

  const identityBlock = (
    <div className="mb-3 small">
      <div>
        <strong>User:</strong> {displayName} (<code>{userId}</code>)
      </div>
      <div>
        <strong>Email:</strong> {email ?? <em className="text-muted">(none)</em>}
      </div>
      {firebaseEmail !== undefined && (
        <div>
          <strong>Firebase:</strong> {firebaseEmail ?? <em className="text-muted">(none)</em>}
        </div>
      )}
      {providers !== undefined && (
        <div>
          <strong>Providers:</strong>{' '}
          {providers.length > 0 ? providers.join(', ') : <em className="text-muted">(none)</em>}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === 'form' && (
          <>
            <Alert variant="danger" className="py-2">
              <strong>This is permanent and cannot be undone.</strong> It runs the same delete as the in-app
              &quot;delete my account&quot;
            </Alert>

            {identityBlock}

            <Form onSubmit={handleContinue}>
              <Form.Group>
                <Form.Label>
                  Type <code>{DELETE_KEYWORD}</code> to confirm
                </Form.Label>
                <Form.Control
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={DELETE_KEYWORD}
                  isInvalid={confirmText.length > 0 && !deleteWordMatches}
                  autoFocus
                />
                <Form.Control.Feedback type="invalid">Type {DELETE_KEYWORD} exactly.</Form.Control.Feedback>
              </Form.Group>

              <Form.Group>
                <Form.Check
                  type="checkbox"
                  id="delete-ack"
                  label="I understand this permanently deletes the account and its progress."
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
              </Form.Group>

              <div className="d-flex justify-content-end mt-3">
                <Button variant="secondary" onClick={onHide} className="mr-2">
                  Cancel
                </Button>
                <Button type="submit" variant="danger" disabled={!canContinue}>
                  OK
                </Button>
              </div>
            </Form>
          </>
        )}

        {step === 'confirm' && (
          <>
            <Alert variant="danger">
              <strong>Are you sure?</strong> You are about to permanently delete this account. This{' '}
              <strong>cannot be undone</strong> and there is no way to recover it afterwards.
            </Alert>

            {identityBlock}

            {result && (
              <Alert variant={result.variant} className="mt-3">
                {result.text}
              </Alert>
            )}

            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="secondary"
                onClick={() => setStep('form')}
                disabled={loading || !!result}
                className="mr-2"
              >
                Go back
              </Button>
              <Button variant="danger" onClick={handleConfirmDelete} disabled={!canDelete}>
                {loading ? 'Deleting...' : 'Permanently delete'}
              </Button>
            </div>
          </>
        )}
      </Modal.Body>
    </>
  );
}
